import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  FormatComboTierSpendLevelPipe,
  SpendLevelType,
} from '@aptitude/shared-vanilla';

import '../labeled-tag/apt-labeled-tag';

//Eric: Duplicated until it can be removed from this component
interface SpendLevel {
  dollarMarketShare?: number;
  unitMarketShare?: number;
  absoluteDollars?: number;
  absoluteUnits?: number;
  absoluteSpend?: number;
  /* Backend is inconsistent about the type and spendLevelType, when ever you use either one of these variables,
  check for which one is not null based on your data (api)
  example: var foo = spendLevel.type || spendLevel.spendLevelType. */
  type?: SpendLevelType;
  spendLevelType?: SpendLevelType;
}

export interface Tier {
  tier?: {
    id: number;
    name: string;
  };
  id?: number;
  name?: string;
  order: number;
  attainability?: string;
  targetSpendLevel: SpendLevel;
  tierType: string;

  dollarMarketShare?: number | null;
  unitMarketShare?: number | null;
  absoluteDollars?: number | null;
  absoluteUnits?: number | null;
  absoluteSpend?: number | null;
  type?: SpendLevelType;
  spendLevelType?: SpendLevelType;
}

@customElement('apt-tiers-detail')
export class TiersDetail extends LitElement {
  @property({ type: Array, attribute: false })
  tiers: Array<Tier> = [];

  @property({ type: Number })
  tierSelected = 0;

  @property({ type: Boolean })
  displayAttainability = true;

  @property({ type: Boolean })
  displayOptimized = false;

  @property({ type: Object })
  tierDetailSettings = {
    displayUnorderedTiers: false,
    displayUnorderedPricing: false,
  };

  value: number | null = null;
  isPristine = true;
  initialSelected = 0;

  static override styles = css`
    :host {
      --white: #eae8e8;
      --green: #8dc63f;
    }

    .tiers-section {
      flex: 1;
    }
    .tier-header {
      display: flex;
      border-bottom: 0.3px solid var(--white);
      padding: 10px 20px;
    }

    .tier-header .tag {
      align-items: flex-start;
    }

    .tiers-section .list-wrapper {
      max-height: 180px;
      overflow-y: auto;
      display: grid;
      grid-template-columns: auto auto;
      padding: 10px 20px;
    }

    .tiers-section h6 {
      font-size: 1.1em;
      margin: 0;
      padding-right: 20px;
    }

    .list-wrapper .description,
    .list-wrapper .tag {
      display: flex;
      align-items: flex-start;
    }

    .description input {
      margin-right: 10px;
    }

    .description label {
      margin-top: 0;
      margin-bottom: 1.2em;
      word-break: break-word;
    }
  `;

  tier(tier: Tier) {
    let attainability = null;
    let optimizedLabel = null;
    // let tierName = tier.tier?.name ? tier.tier.name : tier.name;
    let description: string | null = null;

    if (this.displayAttainability && tier.attainability !== 'ATTAINABLE') {
      attainability = html`<div class="tag">
        <apt-labeled-tag>Unobtainable</apt-labeled-tag>
      </div>`;
    }

    if (this.displayOptimized) {
      if (this.isPristine) {
        if (tier.order === this.tierSelected) {
          optimizedLabel = html`<span class="tag">
            <apt-labeled-tag color="warning">Optimized</apt-labeled-tag>
          </span>`;
        }
      } else if (
        tier.order === this.tierSelected &&
        tier.order !== this.initialSelected
      ) {
        optimizedLabel = html`<span class="tag">
          <apt-labeled-tag color="danger">Reoptimize Mix</apt-labeled-tag>
        </span>`;
      } else if (tier.order === this.initialSelected) {
        optimizedLabel = html`<span class="tag">
          <apt-labeled-tag color="warning">Optimized</apt-labeled-tag>
        </span>`;
      }
    }

    description = this.description(tier);

    return html`
      <div class="description">
        <label data-test-selector="tier-description">
          <input
            type="radio"
            name="tier"
            .value="${tier.order.toString()}"
            @change=${this.handleSelection}
            ?checked="${tier.order === this.tierSelected}"
          />${description}
        </label>
      </div>
      <div class="tag">
        ${attainability ?? html`<span></span>`}
        ${optimizedLabel ?? html`<span></span>`}
      </div>
    `;
  }

  description(tier: Tier) {
    // Figure out if we're using new tier data or old tier data. If it's new, make it look like the old so it's
    // compatible with the transform code.
    if (!tier.targetSpendLevel) {
      const newTier = tier as SpendLevel;

      tier.targetSpendLevel = {
        type: newTier.spendLevelType,
        dollarMarketShare: newTier.dollarMarketShare,
        unitMarketShare: newTier.unitMarketShare,
        absoluteDollars: newTier.absoluteDollars,
        absoluteUnits: newTier.absoluteUnits,
      };
    }

    return FormatComboTierSpendLevelPipe.transform(tier.targetSpendLevel, {
      isPrivate: tier.tierType === 'PRIVATE',
      order: tier.order,
      hidePrivateSeparator: true,
    });
  }

  override render() {
    let unordered = null;

    if (
      this.tierDetailSettings?.displayUnorderedTiers ||
      this.tierDetailSettings?.displayUnorderedPricing
    ) {
      let unorderedText = '';
      if (
        this.tierDetailSettings.displayUnorderedTiers &&
        this.tierDetailSettings.displayUnorderedPricing
      ) {
        unorderedText = 'Unordered Tiers and Pricing';
      } else if (
        this.tierDetailSettings.displayUnorderedTiers &&
        !this.tierDetailSettings.displayUnorderedPricing
      ) {
        unorderedText = 'Unordered Tiers';
      } else if (
        !this.tierDetailSettings.displayUnorderedTiers &&
        this.tierDetailSettings.displayUnorderedPricing
      ) {
        unorderedText = 'Unordered Pricing';
      }

      unordered = html` <div class="tag">
        <apt-labeled-tag color="warning">${unorderedText}</apt-labeled-tag>
      </div>`;
    }

    return html`
      <div class="tiers-detail">
        <form>
          <div class="tiers-section">
            <div class="tier-header">
              <h6>Select your tier:</h6>
              ${unordered ?? html`<span></span>`}
            </div>
            <div class="list-wrapper">
              ${this.tiers?.map((tier) => this.tier(tier))}
            </div>
          </div>
        </form>
      </div>
    `;
  }

  change(newTier: number) {
    if (this.isPristine) {
      this.initialSelected = this.tierSelected;
      this.isPristine = false;
    }
    this.value = newTier;
    this.tierSelected = newTier;
    this.requestUpdate();
  }

  private handleSelection(e: Event | any, tier: any) {
    const tierOrder = Number(e.target.value);
    this.change(tierOrder);
    const event = new CustomEvent<TierEventChange>('onTierChange', {
      bubbles: true,
      detail: {
        tierSelected: this.tierSelected,
      },
    });
    this.dispatchEvent(event);
  }
}

export interface TierEventChange {
  tierSelected: number;
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-tiers-detail': TiersDetail;
  }
}
